import React, { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import ReactECharts from "echarts-for-react";
import AutoCompleteDropdown from "@metacrm/metacrm-material-ui/dist/AutoCompleteDropdown";
import { useIntl } from "react-intl";
import IconTooltip from "@metacrm/metacrm-material-ui/dist/IconTooltip";
import { Loading } from "features/common";
import moment from "moment";
import { formatNumberWithSymbol } from "features/helpers/utils";

const CustomLineChart = ({
  chartTitleKey,
  chartTooltipKey,
  isLoading,
  selectedItems,
  customNodeInOption,
  chartData,
  chartColors,
  itemDropDownProps,
  totalStatsData,
}) => {
  const { formatMessage } = useIntl();
  const chartRef = useRef();

  const { xAxisData, yAxisData, seriesData } = chartData || {};

  const chartOption = {
    animation: false,
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "cross",
        crossStyle: {
          color: "#A5A5A5",
        },
        // Label at axis while showing tooltip
        label: {
          fontSize: 8,
          formatter: (params) => {
            const { value, axisDimension } = params;
            if (axisDimension === "x") {
              return moment(value).format("DD MMM");
            }

            return formatNumberWithSymbol(value);
          },
        },
      },
      position: (point) => ({ left: point[0] + 10, top: point[1] + 30 }), // Fix tooltip to the right
    },
    legend: {
      show: false,
    },
    grid: {
      top: 30,
      bottom: 60,
      left: 15,
      right: 15,
      containLabel: true,
    },
    color: chartColors.slice(0, selectedItems.length),
    xAxis: {
      type: "category",
      data: xAxisData,
      boundaryGap: false,
      // X-axis style
      axisLine: {
        lineStyle: {
          color: "#A5A5A5",
        },
      },
      axisLabel: {
        color: "#A2A3A5",
        fontSize: 8,
        margin: 10,
        formatter: function (value) {
          return moment(value).format("DD MMM");
        },
      },
    },
    yAxis: {
      type: "value",
      // Y-axis style
      axisLabel: {
        formatter: formatNumberWithSymbol,
        fontSize: 8,
      },
    },
    series: seriesData,
  };

  // Avoid chart resize issue
  useEffect(() => {
    setTimeout(() => {
      chartRef.current?.getEchartsInstance().resize();
    }, 100);
  }, []);

  return (
    <Box width="100%">
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
          {chartTitleKey && (
            <Box fontSize={16} color="#555">
              {formatMessage({ id: chartTitleKey })}
            </Box>
          )}
          {chartTooltipKey && (
            <IconTooltip
              content={formatMessage({ id: chartTooltipKey })}
              customColor="#A5A5A5"
            />
          )}
        </Box>
        {itemDropDownProps && (
          <Box sx={{ minWidth: "230px", marginTop: "5px" }}>
            <AutoCompleteDropdown
              size="small"
              value={itemDropDownProps?.options
                ?.filter((option) =>
                  selectedItems?.map((d) => d._id).includes(option.value)
                )
                .sort((a, b) => a.label.localeCompare(b.label))}
              multiple
              options={itemDropDownProps?.options || []}
              onChange={itemDropDownProps?.onChange || (() => {})}
              placeholder={formatMessage({
                id: "global.dualYAxis.selectDataType.placeholder",
              })}
              customNodeInOption={customNodeInOption}
            />
          </Box>
        )}
      </Box>
      <Box height={"310px"} width={"99%"} position={"relative"}>
        {isLoading ? (
          <Loading open={isLoading} fullScreen={false} isChart={true} />
        ) : !yAxisData?.length > 0 ? (
          <Box
            position="absolute"
            width="100%"
            className="item-flex"
            justifyContent="center"
            height="100%"
            flexDirection="column"
          >
            <img src={require("assets/img/barChartAlt.png")} />
            <Box color="#555" fontWeight={700} m="12px 0 8px">
              {formatMessage({ id: "global.chart.emptyStatus.title" })}
            </Box>
            <Box textAlign="center" color="#777" width="188px">
              {formatMessage({ id: "global.chart.emptyStatus.body" })}
            </Box>
          </Box>
        ) : (
          <>
            <Box
              className="item-flex"
              mt="20px"
              sx={{ flexWrap: "nowrap", overflow: "hidden" }}
            >
              {selectedItems.map((kol, index) => {
                const bgColor = chartColors[index];
                return (
                  <Box key={index} mr="32px">
                    <Box
                      className="item-flex"
                      mb="6px"
                      sx={{ flexWrap: "nowrap", whiteSpace: "nowrap" }}
                    >
                      <Box
                        bgcolor={bgColor}
                        height="8px"
                        width="12px"
                        borderRadius="2px"
                        mr="4px"
                      />
                      <Box
                        fontSize={12}
                        fontWeight={400}
                        color="#777"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {kol.name}
                      </Box>
                    </Box>
                    <Box fontSize={18} fontWeight={700} color="#555" pl="16px">
                      {totalStatsData[index]}
                    </Box>
                  </Box>
                );
              })}
            </Box>
            <ReactECharts
              ref={chartRef}
              option={chartOption}
              autoResize={true}
              style={{ height: "100%" }}
              notMerge={true}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default CustomLineChart;
